import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { testSupabaseConnection, testDatabaseAccess, testQuestionsQuery } from '@/lib/supabase-test';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { SUPABASE_URL_EFFECTIVE, SUPABASE_RAW_URL, SUPABASE_OVERRIDE_ACTIVE } from '@/integrations/supabase/client';

export function SupabaseConnectionTest() {
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [dbStatus, setDbStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [questionsStatus, setQuestionsStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [connectionResult, setConnectionResult] = useState<Record<string, unknown> | null>(null);
  const [dbResult, setDbResult] = useState<Record<string, unknown> | null>(null);
  const [questionsResult, setQuestionsResult] = useState<Record<string, unknown> | null>(null);
  const [urlOverride, setUrlOverride] = useState<string>(typeof window !== 'undefined' ? localStorage.getItem('SUPABASE_URL_OVERRIDE') ?? '' : '');
  const [keyOverride, setKeyOverride] = useState<string>(typeof window !== 'undefined' ? localStorage.getItem('SUPABASE_ANON_KEY_OVERRIDE') ?? '' : '');


  const testConnection = async () => {
    setConnectionStatus('testing');
    const result = await testSupabaseConnection();
    setConnectionResult(result);
    setConnectionStatus(result.success ? 'success' : 'error');
  };

  const testDatabase = async () => {
    setDbStatus('testing');
    const result = await testDatabaseAccess();
    setDbResult(result);
    setDbStatus(result.success ? 'success' : 'error');
  };

  const testQuestions = async () => {
    setQuestionsStatus('testing');
    const result = await testQuestionsQuery();
    setQuestionsResult(result);
    setQuestionsStatus(result.success ? 'success' : 'error');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'testing':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'testing':
        return <Badge variant="secondary">Testing...</Badge>;
      case 'success':
        return <Badge variant="default" className="bg-green-500">Connected</Badge>;
      case 'error':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">Not tested</Badge>;
    }
  };

  const applyOverrides = () => {
    if (typeof window === 'undefined') return;
    if (urlOverride) localStorage.setItem('SUPABASE_URL_OVERRIDE', urlOverride.trim());
    if (keyOverride) localStorage.setItem('SUPABASE_ANON_KEY_OVERRIDE', keyOverride.trim());
    location.reload();
  };

  const clearOverrides = () => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('SUPABASE_URL_OVERRIDE');
    localStorage.removeItem('SUPABASE_ANON_KEY_OVERRIDE');
    location.reload();
  };


  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Supabase Connection Test (v2)
        </CardTitle>
        <CardDescription>
          Test your Supabase connection and database access
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Connection Test */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            {getStatusIcon(connectionStatus)}
            <div>
              <h3 className="font-medium">Supabase Connection</h3>
              <p className="text-sm text-muted-foreground">Test basic connection to Supabase</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(connectionStatus)}
            <Button
              onClick={testConnection}
              disabled={connectionStatus === 'testing'}
              size="sm"
            >
              Test Connection
            </Button>
          </div>
        </div>

        {/* Database Test */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            {getStatusIcon(dbStatus)}
            <div>
              <h3 className="font-medium">Products Table</h3>
              <p className="text-sm text-muted-foreground">Test products table access</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(dbStatus)}
            <Button
              onClick={testDatabase}
              disabled={dbStatus === 'testing'}
              size="sm"
            >
              Test Products
            </Button>
          </div>
        </div>

        {/* Questions Test */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            {getStatusIcon(questionsStatus)}
            <div>
              <h3 className="font-medium">Questions Query</h3>
              <p className="text-sm text-muted-foreground">Test questions with product join</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(questionsStatus)}
            <Button
              onClick={testQuestions}
              disabled={questionsStatus === 'testing'}
              size="sm"
            >
              Test Questions
            </Button>
          </div>
        </div>

        {/* Results */}
        {(connectionResult || dbResult || questionsResult) && (
          <div className="space-y-2">
            <h4 className="font-medium">Test Results:</h4>
            {connectionResult && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">Connection Test:</p>
                <pre className="text-xs mt-1 overflow-auto">
                  {JSON.stringify(connectionResult, null, 2)}
                </pre>
              </div>
            )}
            {dbResult && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">Products Test:</p>
                <pre className="text-xs mt-1 overflow-auto">
                  {JSON.stringify(dbResult, null, 2)}
                </pre>
              </div>
            )}
            {questionsResult && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">Questions Test:</p>
                <pre className="text-xs mt-1 overflow-auto">
                  {JSON.stringify(questionsResult, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}


        {/* Dev Overrides (no DevTools needed) */}
        <div className="p-3 bg-yellow-50 rounded-lg">
          <h4 className="font-medium text-yellow-900 mb-2">Dev Overrides</h4>
          <div className="grid gap-2">
            <Input
              placeholder="Supabase URL override (e.g. https://mborlyertmbornltlmdf.supabase.co)"
              value={urlOverride}
              onChange={(e) => setUrlOverride(e.target.value)}
            />
            <Input
              type="password"
              placeholder="Anon key override (paste anon public key)"
              value={keyOverride}
              onChange={(e) => setKeyOverride(e.target.value)}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={applyOverrides}>Apply & Reload</Button>
              <Button size="sm" variant="outline" onClick={clearOverrides}>Clear Overrides</Button>
            </div>
          </div>
        </div>

        {/* Environment Info */}
        <div className="p-3 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Configuration:</h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p><strong>Supabase URL:</strong> {SUPABASE_URL_EFFECTIVE || 'Not configured'}</p>
            <p><strong>Raw URL:</strong> {SUPABASE_RAW_URL || '(empty)'}</p>
            <p><strong>Anon Key:</strong> {import.meta.env.VITE_SUPABASE_ANON_KEY ? '✓ Configured' : '✗ Not configured'}</p>
            <p><strong>Override:</strong> {SUPABASE_OVERRIDE_ACTIVE ? 'ON' : 'OFF'}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
